import { Button, Form, InputNumber, Modal, message } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import FormItem from 'antd/lib/form/FormItem';
import React, { FC, useCallback, useMemo } from 'react';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import { connect } from 'react-redux';
import { equipmentTypesRestCrudThunksForSupplier } from '../../thunks/equipment_types_thunks';
import { stockLocationsRestCrudThunksForSupplier } from '../../thunks/stock_locations_thunks';
import { equipmentTypesPerStockLocationRestCrudThunksForSupplier } from '../../thunks/equipment_types_per_stock_location_thunks';
import { renderEquipmenTypeSelect } from '../purchase_order_form/ReplaceEquipmentLineItemModal';
import { nullSafeGet } from '../../utils/DataAccessUtils';

const AddOrEditEquipmentLevelModal: FC<any> = ({
	onCancel,
	onSuccess,
	stockLocationId,
	creating,
	updating,
	equipmentTypes,
	fetchEquipmentTypes,
	fetchMultipleEquipments,
	updateEquipmentLevel,
	createEquipmentLevel,
	equipmentPerStockLocationLevel,
}): React.ReactElement => {
	const [form] = useForm();

	const isUpdate = useMemo(
		() => nullSafeGet('id', equipmentPerStockLocationLevel),
		[equipmentPerStockLocationLevel]
	);

	const handleAddEquipmentSubmit = useCallback(
		(values) => {
			const entity = {
				...values,
				stockLocationId: parseInt(stockLocationId),
			};

			const submitAction = isUpdate ? updateEquipmentLevel : createEquipmentLevel;

			submitAction(entity).then(() => {
				onSuccess();
				message.success(`${isUpdate ? 'Updated' : 'Added'} successfully!`);
			});
		},
		[createEquipmentLevel, isUpdate, onSuccess, stockLocationId, updateEquipmentLevel]
	);

	return (
		<Modal
			visible
			width={600}
			title={`${isUpdate ? 'Edit' : 'Add'} Equipment Level`}
			onCancel={onCancel}
			closable={false}
			footer={[
				<Button onClick={onCancel} size="large">
					Cancel
				</Button>,
				<Button
					type="primary"
					size="large"
					style={{ marginLeft: '16px' }}
					key="submit"
					htmlType="submit"
					form="add-equipment-form"
					loading={creating || updating}
				>
					{isUpdate ? 'Update' : 'Add Equipment'}
				</Button>,
			]}
		>
			<Form
				form={form}
				id="add-equipment-form"
				layout="vertical"
				requiredMark={false}
				initialValues={equipmentPerStockLocationLevel}
				onFinish={handleAddEquipmentSubmit}
			>
				<Form.Item name="equipment" hidden />
				<FormItem
					name="equipmentTypeId"
					label="Equipment Type"
					rules={[{ required: true, message: 'This field is required.' }]}
				>
					<OWAsyncSelect
						disabled={isUpdate}
						stateSlice={equipmentTypes}
						targetCollectionName="equipmentTypesAtStockLocationIndex"
						fetchMultiple={(ids, targetCollectionName) => {
							fetchMultipleEquipments(ids, targetCollectionName);
						}}
						fetchData={(
							name,
							targetCollectionName,
							pagination,
							sorting,
							filters,
							addToTargetCollection
						) => {
							fetchEquipmentTypes(
								{ search: name },
								targetCollectionName,
								pagination,
								sorting,
								filters,
								addToTargetCollection
							);
						}}
						renderRecord={renderEquipmenTypeSelect}
						sortBy={{ sort_by: 'name', order: 'ascend' }}
					/>
				</FormItem>
				<FormItem name="minQuantity" label="Min Quantity">
					<InputNumber style={{ width: '100%' }} />
				</FormItem>
				<FormItem name="maxQuantity" label="Max Quantity">
					<InputNumber style={{ width: '100%' }} />
				</FormItem>
			</Form>
		</Modal>
	);
};

const mapStateToProps = (state, ownProps) => ({
	visible: ownProps.visible,
	onCancel: ownProps.onCancel,
	onSubmit: ownProps.onSubmit,
	creating: state.equipment_types_per_stock_location.creating,
	updating: state.equipment_types_per_stock_location.updating,

	equipmentTypes: state.equipment_types,
});

const mapDispatchToProps = (dispatch) => ({
	fetchEquipment: (id) => dispatch(equipmentTypesRestCrudThunksForSupplier.readOne(id)),
	fetchEquipmentTypes: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			equipmentTypesRestCrudThunksForSupplier.read(
				{ ...(params || {}), isActive: true },
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchMultipleEquipments: (ids, targetCollectionName) =>
		dispatch(equipmentTypesRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	fetchStockLocations: (params, targetCollectionName) =>
		dispatch(stockLocationsRestCrudThunksForSupplier.read(params, targetCollectionName)),
	fetchMultipleStockLocations: (ids, targetCollectionName) =>
		dispatch(stockLocationsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	createEquipmentLevel: (entity) =>
		dispatch(equipmentTypesPerStockLocationRestCrudThunksForSupplier.create(entity)),
	updateEquipmentLevel: (entity) =>
		dispatch(equipmentTypesPerStockLocationRestCrudThunksForSupplier.update(entity)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddOrEditEquipmentLevelModal);
